/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Stack, Table, Text, rem, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { randomInt } from 'es-toolkit';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@fai/friendli-suite/features/i18n';
import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

import { serverlessModelPricingData } from './pricing-data';

const pricingModalId = 'serverless-endpoints-plan-pricing-modal';

export const openServerlessPricingModal = () => {
  modals.openContextModal({
    modal: 'confirm',
    modalId: pricingModalId,
    title: i18n.t('serverless_endpoints.layout.pricing'),
    innerProps: {
      modalBody: <PricingModalBody />,
      cancelProps: { style: { display: 'none' } },
      labels: { confirm: i18n.t('shared.close') },
    },
  });
};

function PricingModalBody() {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  return (
    <Stack gap={theme.pf.layout.md}>
      <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.TitleMd}>
        {t('team_settings.friendli_serverless_endpoints')}
      </Text>

      <PricingTable>
        <PricingTableContent
          data={serverlessModelPricingData}
          headerLabels={[t('team_settings.model_code'), t('team_settings.price_per_unit')]}
        />
      </PricingTable>
    </Stack>
  );
}

PricingModalBody.displayName = 'PricingModalBody';

function PricingTable({ children }: PropsWithChildren) {
  return (
    <Table
      styles={{
        th: { paddingTop: rem(9.5), paddingBottom: rem(9.5), height: rem(40) },
      }}
    >
      {children}
    </Table>
  );
}

function PricingTableContent({
  data,
  headerLabels,
}: {
  data: typeof serverlessModelPricingData;
  headerLabels: ReactNode[];
}) {
  return (
    <>
      <Table.Thead>
        <Table.Tr>
          {headerLabels.map((label, index) => (
            <Table.Th key={`th-${index + 1}`} w={rem(256)}>
              {label}
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {data.map((datum, index) => (
          <Table.Tr key={randomInt(index, 100_000_000)}>
            {Object.values(datum).map((value) => (
              <Table.Td key={value}>{value}</Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </>
  );
}
